import { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { EntitlementState, ActiveSubscription } from './entitlements';
import { useSubscriptionQuery, subscriptionKeys } from './queries/useSubscriptionQuery';

/**
 * Custom hook for managing user entitlements and subscription access
 *
 * Uses React Query to cache subscription data and eliminate redundant API calls.
 * Checks user authentication status, subscription tier, and access permissions.
 * Automatically updates on auth state changes and includes free trial support.
 *
 * @returns {EntitlementState} Entitlement state object containing:
 * - `isAuthenticated` - Whether user is currently logged in
 * - `isSubscribed` - Whether user has an active paid subscription (null if loading)
 * - `subscriptionTier` - Current subscription tier name or 'Free Trial'
 * - `subscriptionEnd` - ISO date string of subscription/trial end date
 * - `activeSubscriptions` - Array of active subscription details
 * - `hasValidAccess` - Whether user has valid access (subscribed OR active free trial)
 * - `entitlementLoaded` - Whether initial entitlement check has completed
 * - `userId` - Current user's ID if authenticated
 *
 * @example
 * // Check if user has access to premium features
 * function PremiumFeature() {
 *   const { hasValidAccess, entitlementLoaded, subscriptionTier } = useEntitlements();
 *
 *   if (!entitlementLoaded) {
 *     return <LoadingSpinner />;
 *   }
 *
 *   if (!hasValidAccess) {
 *     return <UpgradePrompt />;
 *   }
 *
 *   return <PremiumContent tier={subscriptionTier} />;
 * }
 */
export const useEntitlements = (): EntitlementState => {
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const queryClient = useQueryClient();

  // Track authentication state changes
  useEffect(() => {
    // Get initial user
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id);
      // Invalidate all subscription queries when auth state changes (login/logout)
      // This ensures fresh data is fetched for the new auth state
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  // Use React Query to fetch subscription data with caching
  const { data: subscriptionData, isLoading, error } = useSubscriptionQuery(userId);

  // Compute entitlement state with free trial logic
  const entitlementState = useMemo((): EntitlementState => {
    const isAuthenticated = !!userId;

    // If loading or not authenticated, return default state
    if (!isAuthenticated) {
      return {
        isAuthenticated: false,
        isSubscribed: false,
        entitlementLoaded: !isLoading,
        subscriptionTier: null,
        subscriptionEnd: null,
        activeSubscriptions: [],
        hasValidAccess: false,
        userId: undefined,
      };
    }

    // If still loading, return loading state
    if (isLoading) {
      return {
        isAuthenticated: true,
        isSubscribed: null,
        entitlementLoaded: false,
        subscriptionTier: null,
        subscriptionEnd: null,
        activeSubscriptions: [],
        hasValidAccess: false,
        userId,
      };
    }

    // If error occurred, return error state
    if (error || !subscriptionData) {
      return {
        isAuthenticated: true,
        isSubscribed: false,
        entitlementLoaded: true,
        subscriptionTier: null,
        subscriptionEnd: null,
        activeSubscriptions: [],
        hasValidAccess: false,
        userId,
      };
    }

    // Extract subscription data
    const subscribed = subscriptionData.subscribed;
    let tier = subscriptionData.subscription_tier;
    let end = subscriptionData.current_period_end;
    const activeSubscriptions: ActiveSubscription[] = subscriptionData.subscriptions.map((s) => ({
      id: s.id,
      status: s.status,
      productName: s.product_name,
      currentPeriodEnd: s.current_period_end,
      cancelAtPeriodEnd: s.cancel_at_period_end,
    }));

    // Check if user has valid access (subscribed OR valid free trial)
    let validAccess = subscribed;

    // If not subscribed, check for free trial access
    if (!subscribed && userId) {
      // Check if user has a trial start date stored locally
      const trialStartDate = localStorage.getItem(`trial_start_${userId}`);
      if (trialStartDate) {
        const trialStart = new Date(trialStartDate);
        const now = new Date();
        const daysSinceTrial = (now.getTime() - trialStart.getTime()) / (1000 * 60 * 60 * 24);

        // Free trial is valid for 7 days
        if (daysSinceTrial <= 7) {
          validAccess = true;
          tier = 'Free Trial';
          end = new Date(trialStart.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
        }
      }
    }

    return {
      isAuthenticated: true,
      isSubscribed: subscribed,
      entitlementLoaded: true,
      subscriptionTier: tier,
      subscriptionEnd: end,
      activeSubscriptions,
      hasValidAccess: validAccess,
      userId,
    };
  }, [userId, subscriptionData, isLoading, error]);

  return entitlementState;
};